'use client'
import React, { useImperativeHandle, useRef, type RefObject } from 'react'
import { Picture, Text } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import type { MarqueeProps, MarqueeItemProps, MarqueeImageContentProps, MarqueeTextContentProps } from './type.js'

import { Swiper, SwiperSlide, type SwiperRef } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import { useExposure } from '../../hooks/useExposure.js'

const componentType = 'image'
const componentName = 'marquee'

const Marquee = React.forwardRef<SwiperRef, MarqueeProps>(({ data, className }, ref) => {
  const innerRef = useRef<SwiperRef>(null)
  useImperativeHandle(ref, () => innerRef.current as SwiperRef)

  useExposure(innerRef as unknown as RefObject<HTMLDivElement>, {
    componentType: componentType,
    componentName: componentName,
  })

  return (
    <Swiper
      direction="horizontal"
      className={cn(
        'laptop:h-[80px] h-[48px] w-full items-center [&_.swiper-slide]:!w-auto [&_.swiper-wrapper]:!ease-linear',
        className
      )}
      ref={innerRef}
      id={'Marquee' + data?.key}
      modules={[Autoplay]}
      autoplay={{
        delay: 1,
        disableOnInteraction: false,
        reverseDirection: data?.reverseDirection,
      }}
      loop={true}
      allowTouchMove={false}
      centeredSlides
      slidesPerView="auto"
      speed={data?.speed || 3000}
      breakpoints={{
        0: {
          spaceBetween: '24px',
        },
        1025: {
          spaceBetween: '64px',
        },
      }}
    >
      {data?.items?.map((item, index) => (
        <SwiperSlide key={item?.id + 'SwiperSlide' + index}>
          <MarqueeItem data={item} />
        </SwiperSlide>
      ))}
    </Swiper>
  )
})
Marquee.displayName = 'Marquee'

const MarqueeItem = ({ data, configuration }: MarqueeItemProps) => {
  return (
    <>
      {data.type === 'image' && (
        <MarqueeImageContent image={data?.image} imageClassName={configuration?.imageClassName} />
      )}
      {data.type === 'text' && <MarqueeTextContent text={data?.text} textClassName={configuration?.textClassName} />}
    </>
  )
}

MarqueeItem.displayName = 'MarqueeItem'

const MarqueeImageContent = ({ image, imageClassName }: MarqueeImageContentProps) => {
  return (
    <div className="laptop:w-[120px] h-full w-[72px] shrink-0">
      <Picture source={image?.url} alt={image?.alt || ''} imgClassName={cn('object-cover', imageClassName)} />
    </div>
  )
}

MarqueeImageContent.displayName = 'MarqueeImageContent'

const MarqueeTextContent = React.forwardRef<HTMLDivElement, MarqueeTextContentProps>(({ text, textClassName }, ref) => {
  return (
    <Text
      as="div"
      ref={ref}
      html={text}
      className={cn('text-lines-2 flex size-full items-center justify-center font-medium leading-[1.2]', textClassName)}
    />
  )
})

MarqueeTextContent.displayName = 'MarqueeTextContent'

export default withLayout(Marquee)
export { MarqueeItem, MarqueeImageContent, MarqueeTextContent }
